import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Header, MobileNav } from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { petsAPI } from '../../lib/api';
import { SPECIES } from '../../lib/constants';
import { Plus, Pencil, Trash, Dog, Cat, PawPrint } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export default function ClientPetsPage() {
  const { user } = useAuth();
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    species: '',
    breed: '',
    age: '',
    weight: '',
    photo: '',
    special_needs: ''
  });

  const fetchPets = useCallback(async () => {
    try {
      const data = await petsAPI.getMyPets();
      setPets(data);
    } catch (error) {
      toast.error('Erro ao carregar animais');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPets();
  }, [fetchPets]);

  const resetForm = () => {
    setFormData({
      name: '',
      species: '',
      breed: '',
      age: '',
      weight: '',
      photo: '',
      special_needs: ''
    });
    setEditingPet(null);
  };

  const handleOpenDialog = (pet = null) => {
    if (pet) {
      setEditingPet(pet);
      setFormData({
        name: pet.name || '',
        species: pet.species || '',
        breed: pet.breed || '',
        age: pet.age?.toString() || '',
        weight: pet.weight?.toString() || '',
        photo: pet.photo || '',
        special_needs: pet.special_needs || ''
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.species) {
      toast.error('Nome e espécie são obrigatórios');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null
      };

      if (editingPet) {
        await petsAPI.updatePet(editingPet.pet_id, data);
        toast.success('Animal atualizado!');
      } else {
        await petsAPI.createPet(data);
        toast.success('Animal adicionado!');
      }

      setDialogOpen(false);
      resetForm();
      fetchPets();
    } catch (error) {
      toast.error('Erro ao guardar animal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (petId) => {
    if (!window.confirm('Tem a certeza que desejá remover este animal?')) return;

    try {
      await petsAPI.deletePet(petId);
      toast.success('Animal removido');
      fetchPets();
    } catch (error) {
      toast.error('Erro ao remover animal');
    }
  };

  const PetCard = ({ pet }) => {
    const Icon = pet.species === 'gato' ? Cat : Dog;
    
    return (
      <Card className="provider-card">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <Avatar className="w-16 h-16">
              {pet.photo ? (
                <AvatarImage src={pet.photo} alt={pet.name} />
              ) : null}
              <AvatarFallback>
                <Icon weight="duotone" className="w-8 h-8 text-muted-foreground" />
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">{pet.name}</h3>
              <p className="text-sm text-muted-foreground">
                {pet.breed || (pet.species === 'gato' ? 'Gato' : 'Cão')}
                {pet.age && ` • ${pet.age} ano${pet.age !== 1 ? 's' : ''}`}
                {pet.weight && ` • ${pet.weight}kg`}
              </p>
              {pet.special_needs && (
                <p className="text-sm text-muted-foreground mt-1">
                  <span className="font-medium">Necessidades:</span> {pet.special_needs}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => handleOpenDialog(pet)}
                data-testid={`edit-pet-${pet.pet_id}`}
              >
                <Pencil className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => handleDelete(pet.pet_id)}
                className="text-destructive hover:text-destructive"
                data-testid={`delete-pet-${pet.pet_id}`}
              >
                <Trash className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Header />
      
      <main className="container mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold font-outfit">Meus Animais</h1>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => handleOpenDialog()} className="rounded-full" data-testid="add-pet-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editingPet ? 'Editar Animal' : 'Adicionar Animal'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Nome *</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Nome do animal"
                      data-testid="pet-name-input"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">Espécie *</label>
                    <Select 
                      value={formData.species} 
                      onValueChange={(v) => setFormData({ ...formData, species: v })}
                    >
                      <SelectTrigger data-testid="pet-species-select">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {SPECIES.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">Raça</label>
                    <Input
                      value={formData.breed}
                      onChange={(e) => setFormData({ ...formData, breed: e.target.value })}
                      placeholder="Ex: Labrador, Siamês..."
                      data-testid="pet-breed-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Idade (anos)</label>
                      <Input
                        type="number"
                        value={formData.age}
                        onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                        placeholder="0"
                        data-testid="pet-age-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Peso (kg)</label>
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.weight}
                        onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                        placeholder="0"
                        data-testid="pet-weight-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">URL da Foto</label>
                    <Input
                      value={formData.photo}
                      onChange={(e) => setFormData({ ...formData, photo: e.target.value })}
                      placeholder="https://..."
                      data-testid="pet-photo-input"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">Necessidades Especiais</label>
                    <Textarea
                      value={formData.special_needs}
                      onChange={(e) => setFormData({ ...formData, special_needs: e.target.value })}
                      placeholder="Medicação, alergias, comportamento..."
                      rows={3}
                      data-testid="pet-needs-input"
                    />
                  </div>

                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full rounded-full"
                    data-testid="save-pet-btn"
                  >
                    {submitting ? 'A guardar...' : (editingPet ? 'Guardar Alterações' : 'Adicionar Animal')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {(() => {
            if (loading) {
              return (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <Card key={i} className="h-24 animate-pulse bg-muted" />
                  ))}
                </div>
              );
            }
            if (pets.length === 0) {
              return (
                <Card>
                  <CardContent className="py-12 text-center">
                    <PawPrint weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Nenhum animal registado</h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      Adicione os seus animais para facilitar os pedidos de serviço
                    </p>
                    <Button onClick={() => handleOpenDialog()} className="rounded-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar Primeiro Animal
                    </Button>
                  </CardContent>
                </Card>
              );
            }
            return (
              <div className="space-y-4">
                {pets.map((pet) => (
                  <PetCard key={pet.pet_id} pet={pet} />
                ))}
              </div>
            );
          })()}
        </motion.div>
      </main>

      <MobileNav />
    </div>
  );
}
